import {
  Banner,
  Card,
  CardHead,
  EmptyState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import type { ProactiveSessionItem } from "@/types";
import { compactTime } from "@/utils/format";
import { sessionTypeLabel } from "@/utils/sessionType";
import { resolveSessionQuery, shortSessionId } from "./sessionLookup";

/** The most the sessions endpoint returns in one call — and it has no pages. */
export const SESSION_LIST_LIMIT = 100;

const MEMBERS_SHOWN = 3;

/**
 * Sessions matching the search, most recently active first
 * (fe/CONVERSATIONS.md §3.1).
 *
 * One page of at most `SESSION_LIST_LIMIT`: the endpoint has no cursor, and
 * the way past the cap is a narrower search, not another page.
 */
export function ConversationSessionList({
  items,
  loading,
  error,
  q,
  email,
  onOpen,
}: {
  items: readonly ProactiveSessionItem[];
  loading: boolean;
  error: string;
  q: string | null;
  email: string | null;
  onOpen: (item: ProactiveSessionItem) => void;
}) {
  const filtered = Boolean(q || email);
  const capped = items.length >= SESSION_LIST_LIMIT;
  const hint = loading
    ? "불러오는 중…"
    : `최근 활동순 ${items.length} 건${capped ? ` (최대 ${SESSION_LIST_LIMIT} 건 — 검색어·이메일로 좁혀 주세요)` : ""}`;

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHead title={filtered ? "검색 결과" : "세션"} hint={hint} />
      {error ? (
        <div className="p-4">
          <Banner tone="error">세션 목록을 불러오지 못했습니다. ({error})</Banner>
        </div>
      ) : loading ? (
        <div className="grid gap-2 p-4">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-9" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState title="일치하는 세션이 없습니다." body={emptyBody(q, filtered)} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>세션</TableHead>
              <TableHead className="w-24">유형</TableHead>
              <TableHead>목적</TableHead>
              <TableHead>멤버</TableHead>
              <TableHead className="w-44">마지막 활동</TableHead>
              <TableHead className="w-28">session_id</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <SessionRow key={item.session_id} item={item} onOpen={() => onOpen(item)} />
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

function emptyBody(q: string | null, filtered: boolean) {
  // A short form only ever resolves against this browser's recent lookups;
  // as a search term it matches no name or member. Say so rather than let it
  // read as "no such conversation".
  const lookup = q ? resolveSessionQuery(q, []) : null;
  if (lookup?.kind === "short_unknown") {
    return `줄인 session_id(16진수 ${lookup.hexDigits}자리)는 이 브라우저의 최근 조회에서만 찾습니다 — 처음 여는 대화는 32자리 전체를 붙여넣어 주세요.`;
  }
  return filtered
    ? "검색어나 멤버 이메일을 바꿔서 다시 찾아보세요. 이메일은 정확히 일치해야 합니다."
    : "이 환경에 열린 세션이 없습니다. prod DB 가 설정되지 않은 환경도 빈 목록으로 보입니다.";
}

function SessionRow({
  item,
  onOpen,
}: {
  item: ProactiveSessionItem;
  onOpen: () => void;
}) {
  const members = item.member_emails ?? [];
  const more = members.length - MEMBERS_SHOWN;

  return (
    <TableRow
      tabIndex={0}
      className="cursor-pointer"
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <TableCell className="max-w-72 truncate font-medium">
        {item.display_name || <span className="text-muted-foreground">(이름 없음)</span>}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {sessionTypeLabel(item.session_type)}
      </TableCell>
      <TableCell className="text-xs">{item.run_purpose ?? "unknown"}</TableCell>
      <TableCell className="max-w-96 truncate text-xs" title={members.join(", ")}>
        {members.slice(0, MEMBERS_SHOWN).join(", ")}
        {more > 0 && <span className="text-muted-foreground"> 외 {more}</span>}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground tabular-nums">
        {compactTime(item.last_active_at)}
      </TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground" title={item.session_id}>
        {shortSessionId(item.session_id)}
      </TableCell>
    </TableRow>
  );
}
